import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { axiosPrivate } from '../../modules/networkTools/networkTools';
import { getTimezoneOffsetHours } from '../../utils/dateHelpers/dateHelpers';
import { ScreenerDetails } from './screenerSlice';

export const getScreeners = createAsyncThunk(
  'screener/getScreneers',
  async ({ clientId }: { clientId: string | number }, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get(`clients/${clientId}/screeners`);
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getScreenerQuestions = createAsyncThunk(
  'screener/getScreenerQuestions',
  async (
    { clientId, type }: { clientId: string | number; type: 'impact' },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosPrivate.get(`clients/${clientId}/screener/questions/${type}`);
      return response.data.sections;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getScreenerDetails = createAsyncThunk(
  'screener/getScreenerDetails',
  async (
    { clientId, type }: { clientId: string | number; type: 'impact' },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosPrivate.get(`clients/${clientId}/screener/${type}`);
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const putScreener = createAsyncThunk(
  'screener/putScreener',
  async (
    {
      clientId,
      type,
      screener
    }: { clientId: string | number; type: string; screener: ScreenerDetails },
    { rejectWithValue }
  ) => {
    try {
      const timezoneOffset = getTimezoneOffsetHours(
        screener.dateOfScreening,
        screener.timeOfScreening
      );
      const response = await axiosPrivate.put(`clients/${clientId}/screener/${type}`, screener, {
        headers: {
          'Content-Type': 'application/json',
          'x-time-zone-offset': timezoneOffset
        }
      });
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const patchScreener = createAsyncThunk(
  'screener/patchScreener',
  async (
    {
      clientId,
      type,
      screener
    }: { clientId: string | number; type: string; screener: Partial<ScreenerDetails> },
    { rejectWithValue }
  ) => {
    try {
      const timezoneOffset = getTimezoneOffsetHours(
        screener.dateOfScreening,
        screener.timeOfScreening
      );
      const response = await axiosPrivate.patch(`clients/${clientId}/screener/${type}`, screener, {
        headers: {
          'Content-Type': 'application/json',
          'x-time-zone-offset': timezoneOffset
        }
      });
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getScreenerHistory = createAsyncThunk(
  'screener/getScreenerHistory',
  async (
    { clientId, type }: { clientId: string | number; type: 'impact' },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosPrivate.get(`clients/${clientId}/screener/${type}/history`);
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getOtherScreenerDetails = createAsyncThunk(
  'screener/getOtherScreenerDetails',
  async ({ clientId }: { clientId: string | number }, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get(`clients/${clientId}/screener/other`);
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

type OtherScreener = {
  dateOfScreening?: string;
  timeOfScreening?: string | null;
  needsIdentified?: string[];
  notes?: string;
};

export const putOtherScreener = createAsyncThunk(
  'screener/putOtherScreener',
  async (
    {
      clientId,
      type,
      screener
    }: { clientId: string | number; type: string; screener: OtherScreener },
    { rejectWithValue }
  ) => {
    try {
      const timezoneOffset = getTimezoneOffsetHours(
        screener.dateOfScreening,
        screener.timeOfScreening
      );
      const response = await axiosPrivate.put(`clients/${clientId}/screener/${type}`, screener, {
        headers: {
          'Content-Type': 'application/json',
          'x-time-zone-offset': timezoneOffset
        }
      });
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const patchOtherScreener = createAsyncThunk(
  'screener/patchOtherScreener',
  async (
    {
      clientId,
      type,
      screener
    }: {
      clientId: string | number;
      type: string;
      screener: OtherScreener & { submitted: boolean };
    },
    { rejectWithValue }
  ) => {
    try {
      const timezoneOffset = getTimezoneOffsetHours(
        screener.dateOfScreening,
        screener.timeOfScreening
      );
      const response = await axiosPrivate.patch(`clients/${clientId}/screener/${type}`, screener, {
        headers: {
          'Content-Type': 'application/json',
          'x-time-zone-offset': timezoneOffset
        }
      });
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);

export const getOtherScreenerHistory = createAsyncThunk(
  'screener/getOtherScreenerHistory',
  async ({ clientId }: { clientId: string | number }, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get(`clients/${clientId}/screener/other/history`);
      return response.data;
    } catch (error: any | AxiosError) {
      if (error.response && error.response.data.errors) {
        return rejectWithValue(error.response.data.errors);
      } else {
        return rejectWithValue(error.message);
      }
    }
  }
);
